import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';

// The Meta news feed: what Meta changes on the WhatsApp Business Platform,
// collected by the settings service. Every mutation refreshes the whole
// family, because a read marker moves the list, the totals and the banner.
const ROOT = ['admin', 'meta-news'] as const;

export function useMetaNews(opts: admin.ListMetaNewsOptions) {
  return useQuery({
    queryKey: [...ROOT, 'items', opts],
    queryFn: () => admin.listMetaNews(opts),
    placeholderData: keepPreviousData
  });
}

export function useMetaNewsSummary() {
  return useQuery({
    queryKey: [...ROOT, 'summary'],
    queryFn: () => admin.getMetaNewsSummary(),
    refetchInterval: 5 * 60_000
  });
}

export function useMetaNewsSources() {
  return useQuery({ queryKey: [...ROOT, 'sources'], queryFn: () => admin.listMetaNewsSources() });
}

function useInvalidateMetaNews() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ROOT });
}

export function useMarkMetaNews() {
  const invalidate = useInvalidateMetaNews();
  return useMutation({
    mutationFn: ({ id, read }: { id: string; read: boolean }) =>
      read ? admin.markMetaNewsRead(id) : admin.markMetaNewsUnread(id),
    onSuccess: invalidate
  });
}

export function useMarkAllMetaNewsRead() {
  const invalidate = useInvalidateMetaNews();
  return useMutation({
    mutationFn: (topic?: admin.MetaNewsTopic) => admin.markAllMetaNewsRead(topic ? { topic } : undefined),
    onSuccess: invalidate
  });
}

export function useRefreshMetaNews() {
  const invalidate = useInvalidateMetaNews();
  return useMutation({
    mutationFn: (sourceId?: string) => admin.refreshMetaNews(sourceId ? { source_id: sourceId } : undefined),
    onSuccess: invalidate
  });
}

export function useCreateMetaNewsSource() {
  const invalidate = useInvalidateMetaNews();
  return useMutation({
    mutationFn: (req: admin.CreateMetaNewsSourceRequest) => admin.createMetaNewsSource(req),
    onSuccess: invalidate
  });
}

export function useUpdateMetaNewsSource() {
  const invalidate = useInvalidateMetaNews();
  return useMutation({
    mutationFn: ({ id, ...req }: { id: string } & admin.UpdateMetaNewsSourceRequest) => admin.updateMetaNewsSource(id, req),
    onSuccess: invalidate
  });
}

export function useDeleteMetaNewsSource() {
  const invalidate = useInvalidateMetaNews();
  return useMutation({ mutationFn: (id: string) => admin.deleteMetaNewsSource(id), onSuccess: invalidate });
}
